import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Wallet,
  ArrowDownCircle,
  ArrowUpCircle,
  Users,
  TrendingUp,
  RefreshCw,
  DollarSign,
} from "lucide-react";
import api from "../utils/axios";

/* ================= TYPES ================= */

type LevelKey = "f1" | "f2" | "f3" | "f4" | "f5" | "f6";

type GroupedData = {
  counts: Record<LevelKey | "total", number>;
  list: Record<LevelKey, any[]>;
};

type UserProfile = {
  uid: number | string;
  name: string;
  email: string;
  created_at: string;
  balance: number;

  f1: number;
  f2: number;
  f3: number;
  f4: number;
  f5: number;
  f6: number;
  total_referrals: number;
  today_referrals: number;

  total_commission: number;

  total_deposit: number;
  today_deposit: number;
  total_withdrawal: number;
  today_withdrawal: number;

  total_win_bet_amount: number;
  today_win_bet_amount: number;
  total_lose_bet_amount: number;
  today_lose_bet_amount: number;
};

/* ================= UTILS ================= */

const formatCurrency = (v?: number) =>
  `$${Number(v || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const tabs = [
  { key: "members", label: "Members", icon: Users },
  { key: "deposit", label: "Deposits", icon: ArrowUpCircle },
  { key: "withdrawal", label: "Withdrawals", icon: ArrowDownCircle },
  { key: "bets", label: "Bets", icon: Wallet },
];

/* ================= COMPONENT ================= */

const CollaboratorsProfile: React.FC = () => {
  const  uid  = useParams<{ uid: string }>();

  const [user, setUser] = useState<UserProfile | null>(null);

  const [members, setMembers] = useState<GroupedData | null>(null);
  const [deposits, setDeposits] = useState<GroupedData | null>(null);
  const [withdrawals, setWithdrawals] = useState<GroupedData | null>(null);
  const [betHistory, setBetHistory] = useState<GroupedData | null>(null);

  const [activeTab, setActiveTab] = useState("members");
  const [activeLevel, setActiveLevel] = useState<LevelKey>("f1");

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(true);

  /* ================= FETCH ================= */

  const fetchUser = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/user/${uid?.id}/collaborators/detail`);
      const d = res.data.data;

      setUser({
        uid: d.user.uid,
        name: d.user.name,
        email: d.user.email,
        created_at: d.user.created,
        balance: Number(d.user.balance) || 0,

        f1: d.members.counts.f1,
        f2: d.members.counts.f2,
        f3: d.members.counts.f3,
        f4: d.members.counts.f4,
        f5: d.members.counts.f5,
        f6: d.members.counts.f6,
        total_referrals: d.network_stats.total_referrals,
        today_referrals: d.network_stats.today_referrals || 0,

        total_commission:
          (d.user.total_bet_level_commission || 0) +
          (d.user.total_deposit_level_commission || 0) +
          (d.user.total_referral_reward || 0),

        total_deposit: d.network_stats.total_deposit || 0,
        today_deposit: d.network_stats.today_deposit || 0,
        total_withdrawal: d.network_stats.total_withdrawal || 0,
        today_withdrawal: d.network_stats.today_withdrawal || 0,

        total_win_bet_amount: d.network_stats.total_win_bet_amount || 0,
        today_win_bet_amount: d.network_stats.today_win_bet_amount || 0,
        total_lose_bet_amount: d.network_stats.total_lose_bet_amount || 0,
        today_lose_bet_amount: d.network_stats.today_lose_bet_amount || 0,
      });

      setMembers(d.members);
      setDeposits(d.deposits);
      setWithdrawals(d.withdrawals);
      setBetHistory(d.bet_history);

      setRows(d.members.list.f1 || []);
      setTabLoading(false);
    } catch (err) {
      console.error("User fetch failed", err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= TAB + LEVEL ================= */

  const getActiveData = (): GroupedData | null => {
    if (activeTab === "members") return members;
    if (activeTab === "deposit") return deposits;
    if (activeTab === "withdrawal") return withdrawals;
    if (activeTab === "bets") return betHistory;
    return null;
  };

  useEffect(() => {
    const data = getActiveData();
    setTabLoading(true);
    setRows(data?.list?.[activeLevel] || []);
    setTabLoading(false);
  }, [activeTab, activeLevel, members, deposits, withdrawals, betHistory]);

  useEffect(() => {
    fetchUser();
  }, []);

  /* ================= LOADING ================= */

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-gray-300 border-t-gray-600 rounded-full" />
      </div>
    );
  }

  /* ================= RENDER ================= */

  return (
    <div className="min-h-screen max-w-7xl mx-auto space-y-6">

      {/* HEADER */}
      <div className="flex items-center justify-between">
        
        <div>
        <h1 className="text-2xl font-bold text-gray-800">
          Collaborators Profile
        </h1>
        <p className="text-sm text-gray-500">
          Manage your collaborators here
        </p>
      </div>
        <button
          onClick={fetchUser}
          className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* ================= SUMMARY ================= */}
      <Section title="Overview">
        <StatGrid>
          <StatCard title="Balance" value={formatCurrency(user.balance)} icon={Wallet} />
          <StatCard title="Total Commission" value={formatCurrency(user.total_commission)} icon={DollarSign} />
          <StatCard title="Total Referrals" value={String(user.total_referrals)} icon={Users} />
          <StatCard title="Today Referrals" value={String(user.today_referrals)} icon={TrendingUp} />
        </StatGrid>
      </Section>

      {/* ================= DEPOSITS & WITHDRAWALS ================= */}
      <Section title="Transactions">
        <StatGrid>
          <StatCard title="Total Deposit" value={formatCurrency(user.total_deposit)} icon={ArrowUpCircle} />
          <StatCard title="Today Deposit" value={formatCurrency(user.today_deposit)} icon={ArrowUpCircle} />
          <StatCard title="Total Withdrawal" value={formatCurrency(user.total_withdrawal)} icon={ArrowDownCircle} />
          <StatCard title="Today Withdrawal" value={formatCurrency(user.today_withdrawal)} icon={ArrowDownCircle} />
        </StatGrid>
      </Section>

      {/* ================= BET STATS ================= */}
      <Section title="Bet Statistics">
        <StatGrid>
          <StatCard title="Total Win Bet" value={formatCurrency(user.total_win_bet_amount)} icon={Wallet} />
          <StatCard title="Today Win Bet" value={formatCurrency(user.today_win_bet_amount)} icon={Wallet} />
          <StatCard title="Total Lose Bet" value={formatCurrency(user.total_lose_bet_amount)} icon={Wallet} />
          <StatCard title="Today Lose Bet" value={formatCurrency(user.today_lose_bet_amount)} icon={Wallet} />
        </StatGrid>
      </Section>

      {/* ================= TABS + TABLE (UNCHANGED) ================= */}
      {/* Keep your existing Tabs + Level selector + Table code here */}

      <div className="bg-white rounded-xl shadow border overflow-x-auto">
        <div className="flex border-b">
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`px-6 py-3 flex items-center gap-2 ${
                  activeTab === t.key
                    ? "border-b-2 border-gray-600 font-semibold"
                    : "text-gray-500"
                }`}
              >
                <Icon size={16} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* LEVEL SELECTOR */}
        <div className="flex gap-2 p-4">
          {(["f1","f2","f3","f4","f5","f6"] as LevelKey[]).map(l => (
            <button
              key={l}
              onClick={() => setActiveLevel(l)}
              className={`px-3 py-1 rounded ${
                activeLevel === l ? "bg-gray-600 text-white" : "bg-gray-100"
              }`}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>

        {/* TABLE */}
        <div className="p-4">
          {tabLoading ? (
            <div className="text-center py-8">Loading...</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No records found
            </div>
          ) : (
            <table className="w-full border">
              <thead>
                <tr className="bg-gray-100 text-left">
                  {activeTab === "members" ? (
                    <>
                      <th className="p-3">Name</th>
                      <th className="p-3">Email</th>
                      <th className="p-3">Level</th>
                      <th className="p-3">Joined</th>
                    </>
                  ) : (
                    <>
                      <th className="p-3">Date</th>
                      <th className="p-3">Amount</th>
                      <th className="p-3">Status</th>
                      <th className="p-3">Type</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t">
                    {activeTab === "members" ? (
                      <>
                        <td className="p-3 font-medium">{r.name}</td>
                        <td className="p-3 text-sm text-gray-600">{r.email}</td>
                        <td className="p-3 font-bold">F{r.level}</td>
                        <td className="p-3">
                          {new Date(r.created).toLocaleString()}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="p-3">
                          {new Date(
                            r.transaction_date || r.bet_timestamp
                          ).toLocaleString()}
                        </td>
                        <td className="p-3 font-bold">
                          ${r.amount || r.bet_amount}
                        </td>
                        <td className="p-3 capitalize">{r.status || "-"}</td>
                        <td className="p-3 capitalize">
                          {r.req_type || activeTab}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

/* ================= UI HELPERS ================= */

const Section = ({ title, children }: any) => (
  <div>
    <h2 className="text-lg font-semibold mb-3">{title}</h2>
    {children}
  </div>
);

const StatGrid = ({ children }: any) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{children}</div>
);

const StatCard = ({
  title,
  value,
  icon: Icon,
}: {
  title: string;
  value: string;
  icon: any;
}) => (
  <div className="bg-white p-4 rounded-lg shadow border">
    <div className="flex items-center gap-2 mb-2">
      <Icon size={18} />
      <span className="text-sm font-medium">{title}</span>
    </div>
    <div className="text-xl font-bold">{value}</div>
  </div>
);

export default CollaboratorsProfile;
